import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { apiBase, getTokens, refreshTokens } from './api';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from './components/Toasts';
import { reportClientError } from './components/reportClientError';
import { bootstrapLang } from './i18n';
import { type QueuedWrite, startOutbox } from './offline';
import { queryClient } from './query';
import { router } from './router';
import { applyTheme, watchSystemTheme } from './theme';

// Self-hosted, not the Google Fonts CDN: this is an installed PWA that people
// open in a basement car park, and a heading should not wait on a third party
// to have an opinion. Weights are the ones the design actually uses.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/plus-jakarta-sans/800.css';
import '@fontsource/jetbrains-mono/400.css';
// Hebrew and Arabic-script faces, subset files only: Inter carries the Latin,
// and per-glyph fallback hands Hebrew to Heebo and Arabic script to Noto Sans
// Arabic, so the latin subsets of these would be dead weight.
import '@fontsource/heebo/hebrew-400.css';
import '@fontsource/heebo/hebrew-500.css';
import '@fontsource/heebo/hebrew-600.css';
import '@fontsource/heebo/hebrew-700.css';
import '@fontsource/noto-sans-arabic/arabic-400.css';
import '@fontsource/noto-sans-arabic/arabic-600.css';

// Icon set, bundled — see scripts/build-icons.mjs. Imported before anything
// renders so no icon is ever looked up over the network.
import './icons.generated';

import './styles.css';

bootstrapLang();

// Crash intake: anything that escapes React still reaches the server log
// with the household attached. Registered before the app renders so even a
// boot-time failure is heard.
window.addEventListener('error', (e) => reportClientError('window.onerror', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) =>
  reportClientError('unhandledrejection', e.reason),
);

// PWA service worker (cache shell for offline + faster boot)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* ignore — PWA is opt-in, not load-bearing */
    });
  });
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);

// Replay anything that was written with no signal. Straight fetch rather than
// api(): a replay must not re-enter the queue it came from, and a 401 here is
// for flush() to judge, not a reason to bounce somebody to the login screen
// while their phone is in their pocket.
applyTheme();
watchSystemTheme();

startOutbox(async (w: QueuedWrite) => {
  const attempt = async (accessToken: string | undefined) =>
    fetch(`${apiBase()}${w.path}`, {
      method: w.method,
      headers: {
        ...(w.body ? { 'content-type': 'application/json' } : {}),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        // One key per logical action, stamped at enqueue — the server's dedup
        // for a replay whose first attempt landed but lost its response.
        ...(w.idempotencyKey ? { 'idempotency-key': w.idempotencyKey } : {}),
      },
      ...(w.body ? { body: w.body } : {}),
    });

  let res = await attempt(getTokens()?.access_token);
  if (res.status === 401) {
    // Access tokens live ~15 minutes; a write queued before a tunnel and
    // replayed after it is 401 on age alone, not on merit. Refresh once and
    // send the same idempotency key again — the server dedups if the first
    // attempt actually landed.
    const refreshed = await refreshTokens();
    if (refreshed) res = await attempt(refreshed.access_token);
  }
  return { ok: res.ok, status: res.status };
});
